/*
 * anchor-nav — Level 2 (CSP build) sticky section-nav with IntersectionObserver
 * scroll-spy.
 *
 * LIBRARY TIER (inert): this component is NOT wired into kit chrome and its CSS
 * (resources/css/components/_anchor-nav.css) is imported only by docs.css, never
 * production app.css — mirroring the inert block-library tier. Opt in per project
 * by rendering the partial, importing that CSS into app.css, and giving each
 * target section a matching id.
 *
 * Without JS (or without IntersectionObserver) the nav is just a list of in-page
 * anchor links that jump to each section — fully usable. When JS is present, the
 * link whose section is currently in view gets `.is-active` + aria-current, both
 * toggled imperatively so there are NO per-link Alpine expressions to trip the
 * CSP evaluator (name-only x-data only).
 *
 * Registered in resources/js/app.js as: Alpine.data('anchorNav', anchorNav)
 * Paired with resources/views/components/interactive/anchor-nav.antlers.html.
 */
export default function anchorNav() {
    return {
        _io: null,
        _links: {},

        init() {
            const links = Array.from(this.$root.querySelectorAll('[data-anchor-nav-link]'));
            if (links.length === 0) {
                return;
            }

            const sections = [];
            this._links = {};

            links.forEach((link) => {
                const id = link.getAttribute('data-anchor-nav-link');
                this._links[id] = link;
                const section = document.getElementById(id);
                if (section) {
                    sections.push(section);
                }
            });

            // No IO or no resolvable sections: leave the plain anchors as the
            // fallback (native href jumps still work). Nothing to enhance.
            if (!('IntersectionObserver' in window) || sections.length === 0) {
                return;
            }

            this._io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this.setActive(entry.target.id);
                        }
                    });
                },
                { rootMargin: '0px 0px -70% 0px', threshold: 0 },
            );

            sections.forEach((section) => this._io.observe(section));
        },

        setActive(id) {
            Object.keys(this._links).forEach((key) => {
                const link = this._links[key];
                const isActive = key === id;
                link.classList.toggle('is-active', isActive);
                if (isActive) {
                    link.setAttribute('aria-current', 'true');
                } else {
                    link.removeAttribute('aria-current');
                }
            });
        },

        destroy() {
            if (this._io) {
                this._io.disconnect();
                this._io = null;
            }
            this._links = {};
        },
    };
}
